/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/*
  In this file, we configured URL and componenet for that URL
  #lazy           : Lazy loading is a technique that enables us to load a specific component when a particular route is accessed. 
                    It exponentially enhances the load time and the loading speed. At the same time, it increases the react application performance.
  #React.Suspense : React Suspense here to offer a fallback React element when the component is lazy loaded from the server.
*/
// System defined variables
import React, { lazy, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import _ from 'lodash'

// Custom defined variables.
import {
  caseOverviewDataAtom,
  selectedContractDetails,
} from '../components/recoil/atoms/atoms'
import ManualReviewDetailPage from '@pages/manualReviewTab/ManualReviewDetailPage'
import FuzzyVRM from '@pages/manualReviewTab/features/fuzzyVRM'

const RouteMiddleware = lazy(() => import('./RouteMiddleware'))
const WorkspacePage = lazy(() => import('../pages/home/WorkspacePage'))
const PncPage = lazy(() => import('../pages/pcnPage/PcnPage'))
const ManageUser = lazy(() => import('../pages/manageUsers/ManageUser'))
const AddUser = lazy(() => import('../pages/manageUsers/addUser/AddUserPage'))
const CPZManagement = lazy(() =>
  import('../pages/controlledParkingZone/CPZManagement')
)
const AddCPZ = lazy(() => import('../pages/controlledParkingZone/AddCPZ'))
const AddLocation = lazy(() => import('../pages/location/Location'))
const BailiffList = lazy(() => import('../pages/bailiff/BailiffList'))
const PurgeList = lazy(() => import('../pages/purge/PurgeList'))
const PrefixListPage = lazy(() =>
  import('../pages/prefixListPage/PrefixListPage')
)
const PrefixSetupPage = lazy(() =>
  import('../pages/prefixSetupPage/PrefixSetupPage')
)
const ContractListPage = lazy(() =>
  import('../pages/contract/ContractListPage')
)
const ContactAddress = lazy(() => import('../pages/contactTab/ContactAddress'))
const OrganisationList = lazy(() =>
  import('../pages/organisation/OrganisationList')
)
const OrganisationSetUp = lazy(() =>
  import('../pages/organisation/OrganisationSetUp')
)
const ContraventionList = lazy(() =>
  import('../pages/contravention/ContraventionList')
)
const ContraventionSetupPage = lazy(() =>
  import('../pages/contravention/addpage/ContraventionSetupPage')
)
const CaseOverview = lazy(() => import('../pages/caseOverview/CaseOverview'))
const PageNotFound = lazy(() => import('../pages/pageNotFound/PageNotFound'))
const ContractSetUp = lazy(() => import('../pages/contract/ContractSetUp'))
const FaqList = lazy(() => import('../pages/faq/FaqList'))
const FaqSetup = lazy(() => import('../pages/faq/FaqSetup'))
const TemplatePage = lazy(() => import('../pages/templates/TemplatePage'))
const TemplateSetup = lazy(() =>
  import('../pages/templates/templateTab/TemplateSetup')
)
const SendCorrespondence = lazy(() =>
  import('../pages/pcnPage/SendCorrespondence')
)
const ApprovalQueueList = lazy(() =>
  import('../pages/approvalQueue/ApprovalQueueList')
)
const WorkflowManager = lazy(() =>
  import('../pages/workflowManager/WorkflowManager')
)
const ZoneList = lazy(() => import('../pages/zone/ZoneList'))
const MyWorkFlowMain = lazy(() => import('../pages/myWorkFlow/MyWorkflowMain'))
const OnStreet = lazy(() => import('../pages/messaging/onStreet/OnStreet'))
const BackOffice = lazy(() =>
  import('../pages/messaging/backOffice/BackOffice')
)
const ActivityDays = lazy(() => import('../pages/contract/ActivityDays'))
const DVLAListPage = lazy(() => import('../pages/dvlaList/DVLAList'))
const WarrantList = lazy(() => import('../pages/warrant/WarrantList'))
const AbandonedVehiclesList = lazy(() =>
  import('../pages/abandonedVehicles/AbandonedVehiclesList')
)
const NonIntegratedAbandonedVehiclesList = lazy(() =>
  import('../pages/abandonedVehicles/AbandonedVehiclesList')
)
const PrintBatchList = lazy(() => import('../pages/printBatch/PrintBatchList'))
const DebtList = lazy(() => import('../pages/debt/DebtList'))
const ForeignDebt = lazy(() => import('../pages/foreignDebt/ForeignDebt'))
const ScanImport = lazy(() => import('../pages/scanImport/ScanImport'))
const RepresentationReview = lazy(() =>
  import('../pages/representationReview/RepresentationReview')
)
const SearchPage = lazy(() => import('../pages/searchPage/SearchPage'))
const BailiffBatchList = lazy(() =>
  import('../pages/bailiffBatch/BailiffBatchList')
)
const BailiffMessageList = lazy(() =>
  import('../pages/bailiffMessage/BailiffMessageList')
)
const OperationalInternal = lazy(() =>
  import('../pages/reports/operationalInternal/OperationalInternal')
)
const FinancialReports = lazy(() =>
  import('../pages/reports/financialReports/FinancialReports')
)
const OperationalExternal = lazy(() =>
  import('../pages/reports/operationalExternal/OperationalExternal')
)
const ObligatoryReport = lazy(() =>
  import('../pages/reports/obligatoryReport/ObligatoryReport')
)
const CancellationReason = lazy(() =>
  import('../pages/cancellationReason/CancellationReason')
)
const RejectReason = lazy(() => import('../pages/rejectReason/RejectReason'))
const CaseStatus = lazy(() => import('../pages/reports/caseStatus/CaseStatus'))
const OutboundCorrespondence = lazy(() =>
  import('../pages/reports/outboundCorrespondence/OutboundCorrespondence')
)
const InboundCorrespondence = lazy(() =>
  import('../pages/reports/inboundCorrespondence/InboundCorrespondence')
)
const EnforcementTeam = lazy(() =>
  import('../pages/reports/enforcementTeam/EnforcementTeam')
)
const Financial = lazy(() => import('../pages/reports/financial/Financial'))
const AuditandTracking = lazy(() =>
  import('../pages/reports/auditAndTracking/AuditAndTracking')
)
const Monitoring = lazy(() => import('../pages/reports/monitoring/Monitoring'))
const PerformanceMonitoring = lazy(() =>
  import('../pages/reports/performanceMonitoring/PerformanceMonitoring')
)

// Reveal BI - commented out to disable access
// const RevealDashboardPage = lazy(() =>
//   import('../pages/revealBI/RevealDashboardPage')
// )
// const CeoPathTrackingPage = lazy(() =>
//   import('../pages/revealBI/CeoPathTrackingPage')
// )
// const CeoActivityHeatmapPage = lazy(() =>
//   import('../pages/revealBI/CeoActivityHeatmapPage')
// )

const GroundsList = lazy(() => import('../pages/groundReasons/GroundsList'))
const PreDebt = lazy(() => import('../pages/preDebt/PreDebtList'))
const LocationList = lazy(() => import('../pages/location/LocationList'))
const EnforcementMap = lazy(() =>
  import('../pages/enforcementMap/EnforcementMap')
)
const BulkUpdate = lazy(() => import('../pages/bulkUpdate/BulkUpdate'))
const BulkUpdateList = lazy(() => import('../pages/bulkUpdate/BulkUpdateList'))

const Integration = lazy(() => import('../pages/integration/Integration'))
const IntegrationSetup = lazy(() =>
  import('../pages/integration/IntegrationSetup')
)
const BrandingListPage = lazy(() =>
  import('../pages/branding/BrandingListPage')
)
const BrandingSetup = lazy(() => import('../pages/branding/BrandingSetup'))

const OfficerTracking = lazy(() =>
  import('../pages/officerTracking/OfficerTracking')
)
const HoldReasonsList = lazy(() =>
  import('../pages/holdReasons/HoldReasonsList')
)
const DebtReview = lazy(() => import('../pages/debtReview/DebtReview'))
const ManualCaseCreation = lazy(() =>
  import('../pages/manualCaseCreation/ManualCaseCreation')
)
const PCNNumberReservation = lazy(() =>
  import('../pages/pcnNumberReservation/PCNNumberReservationList')
)

const Tasks = lazy(() => import('../pages/tasks/TasksList'))
const PreViewTasks = lazy(() =>
  import('../pages/tasks/PreviewTask/PreviewTask')
)

const GeoFencingSetUpList = lazy(() =>
  import('../pages/geoFencingSetUp/GeoFencingSetUpList')
)
const GeoFencingSetUp = lazy(() =>
  import('../pages/geoFencingSetUp/GeoFencingSetUp')
)
// Function to check permission (Role/ContractDependent) and render pageNotFound/children
const MonitoringReports = lazy(() =>
  import('../pages/reports/monitoringReports/MonitoringReports')
)
const CancellationReasonList = lazy(() =>
  import('../pages/crConfiguration/cancellationReason/CancellationReasonList')
)
const PoundLocationList = lazy(() =>
  import('../pages/crConfiguration/poundLocation/PoundLocationList')
)
const GeoFenceLog = lazy(() => import('../pages/geoFenceLog/GeoFenceLog'))
const Hotspot = lazy(() => import('../pages/hotspot/Hotspot'))
const TrackingAccess = lazy(() =>
  import('../pages/trackingAccess/TrackingAccess')
)
const PrioritySettingList = lazy(() =>
  import('../pages/crConfiguration/prioritySetting/PrioritySettingsList')
)
const PrioritySetting = lazy(() =>
  import('../pages/crPrioritySettings/PrioritySetting')
)
const TruckList = lazy(() => import('../pages/crConfiguration/truck/TruckList'))
const VehicleType = lazy(() => import('../pages/vehicleType/VehicleTypeList'))
const JobsList = lazy(() => import('../pages/clampAndRemoval/Jobs/JobsList'))
const JobsDetails = lazy(() =>
  import('../pages/clampAndRemoval/jobDetails/JobDetails')
)
const TruckMapping = lazy(() => import('../pages/truckMapping/TruckMapping'))
const PoundsDetailsList = lazy(() =>
  import('../pages/clampAndRemoval/pounds/poundsDetailsList/PoundsDetailsList')
)
const PoundDetails = lazy(() =>
  import('../pages/clampAndRemoval/pounds/poundDetails/PoundDetails')
)
const ChargeList = lazy(() => import('../pages/crCharge/ChargeList'))
const CrReporting = lazy(() => import('../pages/crReporting/CrReporting'))
const BriefingList = lazy(() =>
  import('../pages/messaging/briefingList/BriefingList')
)
const BriefingPreview = lazy(() =>
  import('../pages/messaging/briefingPreviewDetails/BriefingPreviewDetails')
)
const FaultsList = lazy(() => import('../pages/faults/FaultsList'))
const SendBriefing = lazy(() => import('../pages/sendBriefing/SendBriefing'))
const ReleaseReasonList = lazy(() =>
  import('../pages/releaseReason/ReleaseReasonList')
)
const PartPayment = lazy(() => import('../pages/payments/PartPayment'))
const AiraSendCorrespondence = lazy(() =>
  import('../pages/airaSendCorrespondence/AiraSendCorrespondence')
)
const DebtorReview = lazy(() => import('../pages/debtorReview/DebtorReview'))
const PreCases = lazy(() => import('../pages/preCases/PreCases'))
const PrintAdmin = lazy(() => import('../pages/printAdmin/PrintAdmin'))
const CaseLock = lazy(() => import('../pages/caseLock/CaseLockList'))
const LockedCasePage = lazy(() => import('../pages/caseLock/LockedCasePage'))
const BlankWorkspace = lazy(() => import('../pages/home/BlankWorkspace'))

//remove duplicate imports
// Function to check permission (Role/ContractDependent) and render pageNotFound/children

const ReasonConfigurationList = lazy(() =>
  import(
    '../pages/airaConfiguration/reasonConfiguration/ReasonConfigurationList'
  )
)
const QuestionConfigurationList = lazy(() =>
  import(
    '../pages/airaConfiguration/questionConfiguration/QuestionConfigurationList'
  )
)

const PcnTypeCreationList = lazy(() =>
  import('../pages/airaConfiguration/pcnTypeCreation/PcnTypeCreationList')
)
const TeamCreationList = lazy(() =>
  import('../pages/airaConfiguration/teamCreation/TeamCreationList')
)
const WarrantReview = lazy(() => import('../pages/warrantReview/WarrantReview'))
const PreCaseDetailPage = lazy(() =>
  import('../pages/preCaseDetailPage/PreCaseDetailPage')
)
const MasterDataCountPage = lazy(() =>
  import('../pages/reports/masterDataCount/MasterDataCount')
)
const UsageReport = lazy(() =>
  import('../pages/reports/usageReport/UsageReport')
)
const UsageJourney = lazy(() =>
  import('../pages/reports/userJourney/UserJourney')
)
const PaymentPlan = lazy(() => import('../pages/paymentPlan/PaymentPlanList'))
const ReportConfiguration = lazy(() =>
  import('../pages/reportConfiguration/ReportConfiguration')
)
const CCTVSiteList = lazy(() => import('../pages/cctvSite/CCTVSiteList'))
const AddCCTVSitePage = lazy(() => import('../pages/cctvSite/AddCCTVSitePage'))
const DiscardReasonList = lazy(() =>
  import('../pages/discardReasons/DiscardReasonList')
)
const WorkflowList = lazy(() => import('../pages/workflow/WorkflowList'))
const NonWorkingDays = lazy(() =>
  import('../pages/nonWorkingDays/NonWorkingDaysList')
)
const TaskReservationList = lazy(() =>
  import('../pages/taskReservation/TaskReservationList')
)
const ManualReview = lazy(() => import('../pages/manualReview/ManualReview'))
const WorkflowOverviewPage = lazy(() =>
  import('../pages/workflowOverview/WorkflowOverviewPage')
)
const OnHold = lazy(() => import('../pages/onHold/OnHold'))
const DiscardedCasesList = lazy(() =>
  import('../pages/discard/discardedCases/DiscardedCasesList')
)
const PrecaseAllocation = lazy(() =>
  import('../pages/preCaseAllocation/PrecaseAllocation')
)
const AnprOperationalReport = lazy(() =>
  import('../pages/reports/anprOperationalReport/AnprOperationalReport')
)
// const AiraSendCorrespondence = lazy(() =>
//   import('../pages/airaSendCorrespondence/AiraSendCorrespondence')
// )
// Function to check permission (Role/ContractDependent) and render pageNotFound/children

// const ReasonConfigurationList = lazy(() =>
//   import(
//     '../pages/airaConfiguration/reasonConfiguration/ReasonConfigurationList'
//   )
// )
// const QuestionConfigurationList = lazy(() =>
//   import(
//     '../pages/airaConfiguration/questionConfiguration/QuestionConfigurationList'
//   )
// )
// when the page comes under contract based screens, then parameter contractDependent should be true
const renderProtectedComponent = (
  component,
  contractDependent,
  stateDataDependent
) => {
  return (
    <RouteMiddleware
      contractRequired={contractDependent}
      stateDataRequired={stateDataDependent}
    >
      {component}
    </RouteMiddleware>
  )
}

function RoutesFile() {
  const caseOverviewData = useRecoilValue(caseOverviewDataAtom)
  const selectedContractData = useRecoilValue(selectedContractDetails)

  return (
    <React.Suspense fallback={<p className="loadingblock"> </p>}>
      {/* Due to GuardProvider is not supported for current React-Router-DOM creating custom routeMiddleware */}
      <Routes>
        <Route path="/" element={renderProtectedComponent(<WorkspacePage />)} />
        <Route
          path="/contracts"
          element={renderProtectedComponent(<WorkspacePage />)}
        />
        <Route
          path="/pcnpage"
          element={renderProtectedComponent(<PncPage />)}
        />
        <Route
          path="/pcnpage:id"
          element={renderProtectedComponent(<PncPage />)}
        />
        <Route
          path="/search"
          element={renderProtectedComponent(<SearchPage />, true)}
        />
        <Route
          path="/manageuser"
          element={renderProtectedComponent(<ManageUser />)}
        />
        <Route
          path="/adduser"
          element={renderProtectedComponent(<AddUser />)}
        />
        <Route
          path="/cpzlist"
          element={renderProtectedComponent(<CPZManagement />)}
        />
        <Route path="/addcpz" element={renderProtectedComponent(<AddCPZ />)} />
        <Route
          path="/addlocation"
          element={renderProtectedComponent(<AddLocation />)}
        />
        <Route
          path="/caseoverview"
          element={renderProtectedComponent(<CaseOverview />, true)}
        />
        <Route
          path="/prefix"
          element={renderProtectedComponent(<PrefixListPage />)}
        />
        <Route
          path="/prefixsetup"
          element={renderProtectedComponent(<PrefixSetupPage />)}
        />
        <Route
          path="/bailiff"
          element={renderProtectedComponent(<BailiffList />)}
        />
        <Route
          path="/purge"
          element={renderProtectedComponent(<PurgeList />)}
        />
        <Route
          path="/contractListPage"
          element={renderProtectedComponent(<ContractListPage />)}
        />
        <Route
          path="/brandinglistpage"
          element={renderProtectedComponent(<BrandingListPage />)}
        />
        <Route
          path="/contractSetUp"
          element={renderProtectedComponent(<ContractSetUp />)}
        />
        <Route
          path="/contactaddress"
          element={renderProtectedComponent(<ContactAddress />)}
        />
        <Route
          path="/organisationlist"
          element={renderProtectedComponent(<OrganisationList />)}
        />
        <Route
          path="/organisationsetup"
          element={renderProtectedComponent(<OrganisationSetUp />)}
        />
        <Route path="*" element={<PageNotFound />} />
        <Route
          path="/contraventions"
          element={renderProtectedComponent(<ContraventionList />)}
        />
        <Route
          path="/addcontravention"
          element={renderProtectedComponent(<ContraventionSetupPage />)}
        />
        <Route
          path="/templates"
          element={renderProtectedComponent(<TemplatePage />)}
        />
        <Route
          path="/templateSetup"
          element={renderProtectedComponent(<TemplateSetup />)}
        />
        <Route
          path="/faqList"
          element={renderProtectedComponent(<FaqList />)}
        />
        <Route
          path="/faqSetup"
          element={renderProtectedComponent(<FaqSetup />)}
        />
        {/* The pcn values for sendCorrespondence passed through redirect state data. Hence required stateData protection */}
        <Route
          path="/sendCorrespondence"
          element={renderProtectedComponent(
            <SendCorrespondence />,
            false,
            false // changing to false. As got the flow to navigate case->senCorrespondence->to new case(linkedCase)-> back to correspondence with old case. This cannot be handled in existing  atom/state so changed to url params.
          )}
        />
        {/* <Route
          path="/approvalQueueList"
          element={renderProtectedComponent(<ApprovalQueueList />, true)}
        /> */}
        <Route
          path="/approvalQueueList"
          element={renderProtectedComponent(<ApprovalQueueList />, true)}
        />
        <Route
          path="/workflowmanager"
          element={renderProtectedComponent(<WorkflowManager />, true)}
        />
        <Route
          path="/myworkflow"
          element={renderProtectedComponent(<MyWorkFlowMain />, true)}
        />
        <Route
          path="/onstreet"
          element={renderProtectedComponent(<OnStreet />, true)}
        />
        <Route
          path="/backoffice"
          element={renderProtectedComponent(<BackOffice />, true)}
        />
        <Route
          path="/faults"
          element={renderProtectedComponent(<FaultsList />, true)}
        />
        <Route
          path="/activedays"
          element={renderProtectedComponent(<ActivityDays />)}
        />
        <Route path="/zone" element={renderProtectedComponent(<ZoneList />)} />
        <Route
          path="/dvla"
          element={renderProtectedComponent(<DVLAListPage />, true)}
        />
        <Route
          path="/warrant"
          element={renderProtectedComponent(<WarrantList />, true)}
        />
        <Route
          path="/printbatchlist"
          element={renderProtectedComponent(<PrintBatchList />, true)}
        />
        <Route
          path="/debt"
          element={renderProtectedComponent(<DebtList />, true)}
        />
        <Route
          path="/foreigndebt"
          element={renderProtectedComponent(<ForeignDebt />, true)}
        />
        <Route
          path="/scanimport"
          element={renderProtectedComponent(<ScanImport />, true)}
        />
        <Route
          path="/repReview"
          element={renderProtectedComponent(<RepresentationReview />, true)}
        />
        <Route
          path="/bailiffbatch"
          element={renderProtectedComponent(<BailiffBatchList />, true)}
        />
        <Route
          path="/bailiffmessage"
          element={renderProtectedComponent(<BailiffMessageList />, true)}
        />
        <Route
          path="/location"
          element={renderProtectedComponent(<LocationList />)}
        />
        <Route
          path="/enforcementmap"
          element={renderProtectedComponent(<EnforcementMap />)}
        />
        <Route
          path="/operationalInternal"
          element={renderProtectedComponent(<OperationalInternal />, true)}
        />
        <Route
          path="/financialReports"
          element={renderProtectedComponent(<FinancialReports />, true)}
        />
        <Route
          path="/operationalexternal"
          element={renderProtectedComponent(<OperationalExternal />, true)}
        />
        <Route
          path="/obligatoryreport"
          element={renderProtectedComponent(<ObligatoryReport />, true)}
        />
        <Route
          path="/casestatus"
          element={renderProtectedComponent(<CaseStatus />, true)}
        />
        <Route
          path="/outboundcorrespondence"
          element={renderProtectedComponent(<OutboundCorrespondence />, true)}
        />
        <Route
          path="/inboundcorrespondence"
          element={renderProtectedComponent(<InboundCorrespondence />, true)}
        />
        <Route
          path="/enforcementteam"
          element={renderProtectedComponent(<EnforcementTeam />, true)}
        />
        <Route
          path="/financial"
          element={renderProtectedComponent(<Financial />, true)}
        />
        <Route
          path="/auditandtracking"
          element={renderProtectedComponent(<AuditandTracking />, true)}
        />
        <Route
          path="/monitoring"
          element={renderProtectedComponent(<Monitoring />, true)}
        />
        <Route
          path="/performancemonitoring"
          element={renderProtectedComponent(<PerformanceMonitoring />, true)}
        />
        {/* Reveal BI Dashboard Routes - commented out to disable access */}
        {/* <Route
          path="/revealdashboard"
          element={renderProtectedComponent(<RevealDashboardPage />, true)}
        />
        <Route
          path="/ceo-path-tracking"
          element={renderProtectedComponent(<CeoPathTrackingPage />, true)}
        />
        <Route
          path="/ceo-activity-heatmap"
          element={renderProtectedComponent(<CeoActivityHeatmapPage />, true)}
        /> */}
        <Route
          path="/cancellationreason"
          element={renderProtectedComponent(<CancellationReason />, false)}
        />
        <Route
          path="/rejectreason"
          element={renderProtectedComponent(<RejectReason />, false)}
        />
        <Route
          path="/groundslist"
          element={renderProtectedComponent(<GroundsList />, false)}
        />
        {caseOverviewData?.isPreDebtCleanse && (
          <Route
            path="/predebt"
            element={renderProtectedComponent(<PreDebt />, false)}
          />
        )}
        <Route
          path="/bulkupdate"
          element={renderProtectedComponent(<BulkUpdate />, true)}
        />
        <Route
          path="/integration"
          element={renderProtectedComponent(<Integration />, false)}
        />
        <Route
          path="/integrationsetup"
          element={renderProtectedComponent(<IntegrationSetup />, false)}
        />
        <Route
          path="/brandingsetup"
          element={renderProtectedComponent(<BrandingSetup />)}
        />
        <Route
          path="/bulkupdatelist"
          element={renderProtectedComponent(<BulkUpdateList />, true)}
        />
        {selectedContractData?.isTrackingMenuEnabled && (
          <Route
            path="/officertracking"
            element={renderProtectedComponent(<OfficerTracking />, true)}
          />
        )}
        <Route
          path="/holdreasonslist"
          element={renderProtectedComponent(<HoldReasonsList />, false)}
        />
        <Route
          path="/debtreview"
          element={renderProtectedComponent(<DebtReview />, true)}
        />
        <Route
          path="/manualcasecreation"
          element={renderProtectedComponent(<ManualCaseCreation />, true)}
        />
        <Route
          path="/casenumberreservation"
          element={renderProtectedComponent(<PCNNumberReservation />, false)}
        />
        <Route
          path="/monitoringreports"
          element={renderProtectedComponent(<MonitoringReports />, true)}
        />
        <Route
          path="/tasks"
          element={renderProtectedComponent(<Tasks />, true)}
        />
        <Route
          path="/previewtasks"
          element={renderProtectedComponent(<PreViewTasks />, true)}
        />
        <Route
          path="/cancellationreasonlist"
          element={renderProtectedComponent(<CancellationReasonList />, false)}
        />
        <Route
          path="/geofencingsetuplist"
          element={renderProtectedComponent(<GeoFencingSetUpList />)}
        />
        <Route
          path="/geofencingsetup"
          element={renderProtectedComponent(<GeoFencingSetUp />)}
        />
        <Route
          path="/poundlocation"
          element={renderProtectedComponent(<PoundLocationList />, false)}
        />
        <Route
          path="/geofencelog"
          element={renderProtectedComponent(<GeoFenceLog />, true)}
        />
        <Route
          path="/hotspot"
          element={renderProtectedComponent(<Hotspot />, true)}
        />
        <Route
          path="/trackingaccess"
          element={renderProtectedComponent(<TrackingAccess />, true)}
        />
        <Route
          path="/prioritysettingslist"
          element={renderProtectedComponent(<PrioritySettingList />, false)}
        />
        <Route
          path="/prioritysettings"
          element={renderProtectedComponent(<PrioritySetting />, false)}
        />
        <Route
          path="/trucklist"
          element={renderProtectedComponent(<TruckList />, false)}
        />
        <Route
          path="/vehicletype"
          element={renderProtectedComponent(<VehicleType />, false)}
        />
        <Route
          path="/jobs"
          element={renderProtectedComponent(<JobsList />, true)}
        />
        <Route
          path="/jobdetails"
          element={renderProtectedComponent(<JobsDetails />, false)}
        />
        <Route
          path="/truckmapping"
          element={renderProtectedComponent(<TruckMapping />, true)}
        />
        <Route
          path="/pounds"
          element={renderProtectedComponent(<PoundsDetailsList />, true)}
        />
        <Route
          path="/pounddetails"
          element={renderProtectedComponent(<PoundDetails />, false)}
        />
        <Route
          path="/chargelist"
          element={renderProtectedComponent(<ChargeList />, false)}
        />
        <Route
          path="sendbriefing"
          element={renderProtectedComponent(<SendBriefing />, true)}
        />
        <Route
          path="/briefinglist"
          element={renderProtectedComponent(<BriefingList />, true)}
        />
        <Route
          path="/crreporting"
          element={renderProtectedComponent(<CrReporting />, true)}
        />
        <Route
          path="/releasereasonlist"
          element={renderProtectedComponent(<ReleaseReasonList />, false)}
        />
        <Route
          path="/briefingpreview"
          element={renderProtectedComponent(<BriefingPreview />, true)}
        />
        <Route
          path="/partpayment"
          element={renderProtectedComponent(<PartPayment />, true)}
        />
        <Route
          path="/precases"
          element={renderProtectedComponent(<PreCases />, true)}
        />
        <Route
          path="/precasedetail"
          element={renderProtectedComponent(<PreCaseDetailPage />)}
        />
        <Route
          path="/reportconfiguration"
          element={renderProtectedComponent(<ReportConfiguration />, false)}
        />
        <Route
          path="/printadmin"
          element={renderProtectedComponent(<PrintAdmin />, false)}
        />
        <Route
          path="/caselock"
          element={renderProtectedComponent(<CaseLock />, false)}
        />
        <Route
          path="/lockedcase"
          element={renderProtectedComponent(<LockedCasePage />, false)}
        />
        {/*  */}

        <Route
          path="/airasendcorrespondence"
          element={renderProtectedComponent(<AiraSendCorrespondence />, true)}
        />
        <Route
          path="/debtorreview"
          element={renderProtectedComponent(<DebtorReview />, true)}
        />
        <Route
          path="/reasonconfiguration"
          element={renderProtectedComponent(<ReasonConfigurationList />, false)}
        />
        <Route
          path="/teamcreation"
          element={renderProtectedComponent(<TeamCreationList />, false)}
        />
        <Route
          path="/questionmasterlist"
          element={renderProtectedComponent(
            <QuestionConfigurationList />,
            false
          )}
        />
        <Route
          path="/pcntypecreation"
          element={renderProtectedComponent(<PcnTypeCreationList />, false)}
        />
        <Route
          path="/warrantreview"
          element={renderProtectedComponent(<WarrantReview />, true)}
        />

        <Route
          path="/masterdatacount"
          element={renderProtectedComponent(<MasterDataCountPage />, false)}
        />
        <Route
          path="/usagereport"
          element={renderProtectedComponent(<UsageReport />, false)}
        />
        <Route
          path="/userjourney"
          element={renderProtectedComponent(<UsageJourney />, false)}
        />
        <Route
          path="/paymentplan"
          element={renderProtectedComponent(<PaymentPlan />, true)}
        />
        <Route
          path="/discardreasonlist"
          element={renderProtectedComponent(<DiscardReasonList />, false)}
        />
        <Route
          path="/workflowlist"
          element={renderProtectedComponent(<WorkflowList />, false)}
        />
        <Route
          path="/orgreport"
          element={renderProtectedComponent(<BlankWorkspace />)}
        />
        <Route
          path="/nonworkingdays"
          element={renderProtectedComponent(<NonWorkingDays />, false)}
        />
        <Route
          path="/cctvsite/list"
          element={renderProtectedComponent(<CCTVSiteList />)}
        />
        <Route
          path="/taskreservationlist"
          element={renderProtectedComponent(<TaskReservationList />)}
        />
        <Route
          path="/cctvsite/add"
          element={renderProtectedComponent(<AddCCTVSitePage />)}
        />
        <Route
          path="/cctvsite/edit"
          element={renderProtectedComponent(<AddCCTVSitePage />)}
        />
        <Route
          path="/workflowoverview"
          element={renderProtectedComponent(<WorkflowOverviewPage />, true)}
        />
        <Route
          path="/precaseallocation"
          element={renderProtectedComponent(<PrecaseAllocation />, true)}
        />
        <Route
          path="/manualreview"
          element={renderProtectedComponent(<ManualReview />, true)}
        />
        <Route
          path="/onhold"
          element={renderProtectedComponent(<OnHold />, true)}
        />
        <Route
          path="/discardedcases"
          element={renderProtectedComponent(<DiscardedCasesList />, true)}
        />
        <Route
          path="/anproperationalreport"
          element={renderProtectedComponent(<AnprOperationalReport />, true)}
        />
        <Route
          path="/manualreviewpage"
          element={renderProtectedComponent(<ManualReviewDetailPage />, false)}
        />
        <Route
          path="/fuzzyvrm"
          element={renderProtectedComponent(<FuzzyVRM />)}
        />
        <Route
          path="/abandonedvehicles"
          element={renderProtectedComponent(<AbandonedVehiclesList />, true)}
        />
        <Route
          path="/nonintegratedabandonedvehicles"
          element={renderProtectedComponent(
            <NonIntegratedAbandonedVehiclesList />,
            true
          )}
        />
      </Routes>
    </React.Suspense>
  )
}

export default RoutesFile
